import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, formatApiErrorDetail } from '../lib/api'
import { getApiBase } from '../lib/utils'
import { useAppLanguage } from '../i18n'

type CatalogModel = {
  id: string
  description?: string | null
  enabled: boolean
  sort_order: number
  size?: number | null
  modified_at?: unknown
  digest?: string | null
}

type CatalogProvider = { id: string; models: CatalogModel[] }
type CatalogSnapshot = { providers: CatalogProvider[] }

type TabId = 'ollama' | 'openai' | 'anthropic'

function flattenOffers(snapshot: CatalogSnapshot) {
  return snapshot.providers.flatMap(p =>
    p.models.map(m => ({
      provider: p.id,
      model_id: m.id,
      enabled: m.enabled,
      sort_order: m.sort_order,
    }))
  )
}

export default function ModelsPage() {
  const { t } = useAppLanguage()
  const mp = t.modelsPlatformAdmin
  const navigate = useNavigate()

  const [snapshot, setSnapshot] = useState<CatalogSnapshot | null>(null)
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [catalogError, setCatalogError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<TabId>('ollama')

  const [pulling, setPulling] = useState(false)
  const [modelName, setModelName] = useState('llama3.2:3b')
  const [deleting, setDeleting] = useState<string | null>(null)
  const [settingDefault, setSettingDefault] = useState<string | null>(null)
  const [streamLog, setStreamLog] = useState<string>('')
  const streamAbortRef = useRef<AbortController | null>(null)
  const [defaultModel, setDefaultModel] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true)
    setCatalogError(null)
    try {
      const { data } = await api.get<CatalogSnapshot>('/admin/platform-llm-catalog')
      setSnapshot(data)
    } catch (e: unknown) {
      setCatalogError(formatApiErrorDetail(e, mp.loadError))
      setSnapshot(null)
    } finally {
      setCatalogLoading(false)
    }
  }, [mp.loadError])

  useEffect(() => {
    loadCatalog()
  }, [loadCatalog])

  useEffect(() => {
    api.get('/ollama/default').then(
      res => {
        const m = res?.data?.model || null
        setDefaultModel(m)
        if (m) {
          try {
            localStorage.setItem('ollama_default_model', m)
          } catch {}
        }
      },
      () => {}
    )
    try {
      const local = localStorage.getItem('ollama_default_model')
      if (local) setDefaultModel(local)
    } catch {}
  }, [])

  const fmtBytes = (n?: number | null) => {
    if (!n || n <= 0) return ''
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let i = 0
    let v = n
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024
      i++
    }
    return `${v.toFixed(1)} ${units[i]}`
  }

  const fmtDate = (s?: unknown) => {
    if (s == null || s === '') return ''
    try {
      return new Date(String(s)).toLocaleString()
    } catch {
      return String(s)
    }
  }

  const cleanModelName = (name: string): string => {
    let cleaned = name.trim()
    for (const prefix of ['ollama run ', 'ollama pull ']) {
      if (cleaned.toLowerCase().startsWith(prefix)) {
        cleaned = cleaned.slice(prefix.length).trim()
      }
    }
    return cleaned
  }

  const pullStream = async (name: string) => {
    const ctrl = new AbortController()
    streamAbortRef.current = ctrl
    const token = localStorage.getItem('access_token') || ''
    const res = await fetch(`${window.location.origin}${getApiBase()}/ollama/pull_stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ model: name }),
      signal: ctrl.signal,
    })
    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let acc = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      acc += decoder.decode(value, { stream: true })
      setStreamLog(acc)
    }
  }

  const pull = async () => {
    if (!modelName.trim()) return
    const cleanedName = cleanModelName(modelName)
    setPulling(true)
    setStreamLog('')
    try {
      await pullStream(cleanedName)
      await loadCatalog()
      setStatus(`${mp.pullTitle}: ${cleanedName}`)
    } catch {
      try {
        await api.post('/ollama/pull', { model: cleanedName })
        await loadCatalog()
        setStatus(`${mp.pullTitle}: ${cleanedName}`)
      } catch (ee: unknown) {
        alert(formatApiErrorDetail(ee, mp.saveError))
      }
    } finally {
      setPulling(false)
      streamAbortRef.current = null
    }
  }

  const del = async (name: string) => {
    if (!confirm(`${mp.delete} ${name} ?`)) return
    setDeleting(name)
    try {
      await api.post('/ollama/delete', { model: name })
      await loadCatalog()
    } catch (e: unknown) {
      alert(formatApiErrorDetail(e, mp.saveError))
    } finally {
      setDeleting(null)
    }
  }

  const setDefault = async (name: string) => {
    setSettingDefault(name)
    try {
      await api.post('/ollama/default', { model: name })
      setDefaultModel(name)
      try {
        localStorage.setItem('ollama_default_model', name)
      } catch {}
      try {
        window.dispatchEvent(new CustomEvent('ollama-default-changed', { detail: name }))
      } catch {}
      setStatus(mp.currentDefault.replace('{name}', name))
    } catch (e: unknown) {
      setCatalogError(formatApiErrorDetail(e, mp.saveError))
    } finally {
      setSettingDefault(null)
    }
  }

  const setModelEnabled = (providerId: string, modelId: string, enabled: boolean) => {
    setSnapshot(prev => {
      if (!prev) return prev
      return {
        providers: prev.providers.map(p => {
          if (p.id !== providerId) return p
          return {
            ...p,
            models: p.models.map(m => (m.id === modelId ? { ...m, enabled } : m)),
          }
        }),
      }
    })
  }

  const setModelSort = (providerId: string, modelId: string, sort_order: number) => {
    setSnapshot(prev => {
      if (!prev) return prev
      return {
        providers: prev.providers.map(p => {
          if (p.id !== providerId) return p
          return {
            ...p,
            models: p.models.map(m => (m.id === modelId ? { ...m, sort_order } : m)),
          }
        }),
      }
    })
  }

  const saveCatalog = async () => {
    if (!snapshot) return
    setSaving(true)
    setCatalogError(null)
    try {
      const { data } = await api.put<CatalogSnapshot>('/admin/platform-llm-catalog', {
        offers: flattenOffers(snapshot),
      })
      setSnapshot(data)
      setStatus(mp.save)
    } catch (e: unknown) {
      setCatalogError(formatApiErrorDetail(e, mp.saveError))
    } finally {
      setSaving(false)
    }
  }

  const currentProvider = useMemo(() => {
    if (!snapshot) return null
    return snapshot.providers.find(p => p.id === activeTab) || null
  }, [snapshot, activeTab])

  const tabs: { id: TabId; label: string }[] = useMemo(
    () => [
      { id: 'ollama', label: mp.tabOllama },
      { id: 'openai', label: mp.tabOpenai },
      { id: 'anthropic', label: mp.tabAnthropic },
    ],
    [mp.tabAnthropic, mp.tabOpenai, mp.tabOllama]
  )

  return (
    <div className="relative overflow-hidden min-h-screen">
      <div
        aria-hidden
        className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]"
      />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-4 px-1 py-3 sm:px-6 sm:py-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 bg-white/80 px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm transition hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/70 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {mp.backAdmin}
          </button>
          <button
            type="button"
            onClick={saveCatalog}
            disabled={saving || !snapshot || catalogLoading}
            className="rounded-full bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:bg-emerald-600 disabled:opacity-50"
          >
            {saving ? mp.saving : mp.save}
          </button>
        </div>

        <header className="rounded-3xl border border-emerald-100/70 bg-white/90 p-6 shadow-xl dark:border-emerald-900/50 dark:bg-zinc-950/90">
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white">{mp.pageTitle}</h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{mp.pageSubtitle}</p>
        </header>

        {status && (
          <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/80 px-4 py-2 text-sm text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200">
            {status}
          </div>
        )}
        {catalogError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {catalogError}
          </div>
        )}

        <section className="rounded-3xl border border-emerald-100/70 bg-white/90 p-4 shadow-xl sm:p-8 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          <div className="mb-6 flex gap-1 overflow-x-auto border-b border-zinc-200/80 pb-2 dark:border-zinc-800">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                  activeTab === tab.id
                    ? 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20'
                    : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {catalogLoading ? (
            <div className="py-12 text-center text-zinc-500">{mp.loading}</div>
          ) : !snapshot ? (
            <div className="py-12 text-center text-red-600 dark:text-red-300">{mp.loadError}</div>
          ) : activeTab === 'ollama' ? (
            <div className="space-y-6">
              {defaultModel && (
                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                  {mp.currentDefault.replace('{name}', defaultModel)}
                </p>
              )}
              <div className="rounded-2xl border border-zinc-200/80 bg-zinc-50/80 p-4 dark:border-zinc-800 dark:bg-zinc-900/40">
                <div className="mb-2 text-sm font-medium text-zinc-800 dark:text-zinc-200">{mp.pullTitle}</div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={modelName}
                    onChange={e => setModelName(e.target.value)}
                    className="min-w-[12rem] flex-1 rounded-full border border-emerald-100/80 bg-white px-4 py-2 text-sm dark:border-emerald-900/50 dark:bg-zinc-950"
                    placeholder={mp.pullPlaceholder}
                    disabled={pulling}
                  />
                  <button
                    type="button"
                    onClick={pull}
                    disabled={pulling || !modelName.trim()}
                    className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {pulling ? mp.pulling : mp.pullButton}
                  </button>
                </div>
                <p className="mt-2 text-xs text-zinc-500">{mp.pullStorageHint}</p>
                {pulling && (
                  <pre className="mt-3 max-h-40 overflow-auto rounded-xl bg-black/80 p-3 text-xs text-emerald-200">
                    {streamLog || '…'}
                  </pre>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-emerald-100/70 text-left text-xs font-medium uppercase tracking-wide text-emerald-600 dark:border-emerald-900/40 dark:text-emerald-400">
                      <th className="py-3 pr-3">{mp.colName}</th>
                      <th className="py-3 pr-3">{mp.colSize}</th>
                      <th className="py-3 pr-3">{mp.colModified}</th>
                      <th className="py-3 pr-3">{mp.tenantSwitch}</th>
                      <th className="py-3 pr-3">{mp.sortLabel}</th>
                      <th className="py-3 pr-3 text-right">{mp.colActions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(currentProvider?.models || []).length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-6 text-zinc-500">
                          {mp.noModels}
                        </td>
                      </tr>
                    )}
                    {(currentProvider?.models || []).map(m => {
                      const isDef = defaultModel === m.id
                      return (
                        <tr key={m.id} className="border-b border-zinc-100 dark:border-zinc-800">
                          <td className="py-2 pr-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-zinc-900 dark:text-zinc-100">{m.id}</span>
                              {isDef && (
                                <span className="rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                                  {mp.defaultBadge}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 pr-3 whitespace-nowrap">{fmtBytes(m.size)}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(m.modified_at)}</td>
                          <td className="py-2 pr-3">
                            <input
                              type="checkbox"
                              checked={m.enabled}
                              onChange={e => setModelEnabled('ollama', m.id, e.target.checked)}
                              className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                            />
                          </td>
                          <td className="py-2 pr-3">
                            <input
                              type="number"
                              className="w-20 rounded-full border border-zinc-200 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-950"
                              value={m.sort_order}
                              onChange={e =>
                                setModelSort('ollama', m.id, Number(e.target.value) || 0)
                              }
                            />
                          </td>
                          <td className="py-2 pr-3 text-right">
                            <div className="flex flex-wrap justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setDefault(m.id)}
                                className="rounded-full border border-zinc-200 px-3 py-1 text-xs disabled:opacity-50 dark:border-zinc-700"
                                disabled={settingDefault === m.id || isDef}
                              >
                                {isDef ? mp.defaultBadge : settingDefault === m.id ? '…' : mp.setDefault}
                              </button>
                              <button
                                type="button"
                                onClick={() => del(m.id)}
                                className="rounded-full border border-red-200 px-3 py-1 text-xs text-red-700 disabled:opacity-50 dark:border-red-900 dark:text-red-300"
                                disabled={!!deleting}
                              >
                                {deleting === m.id ? mp.deleting : mp.delete}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-emerald-100/70 text-left text-xs font-medium uppercase tracking-wide text-emerald-600 dark:border-emerald-900/40 dark:text-emerald-400">
                    <th className="py-3 pr-3">{mp.colName}</th>
                    <th className="py-3 pr-3">{mp.tenantSwitch}</th>
                    <th className="py-3 pr-3">{mp.sortLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {(currentProvider?.models || []).map(m => (
                    <tr key={m.id} className="border-b border-zinc-100 dark:border-zinc-800">
                      <td className="py-2 pr-3">
                        <div className="font-medium text-zinc-900 dark:text-zinc-100">{m.id}</div>
                        {m.description ? (
                          <div className="text-xs text-zinc-500 dark:text-zinc-400">{m.description}</div>
                        ) : null}
                      </td>
                      <td className="py-2 pr-3">
                        <input
                          type="checkbox"
                          checked={m.enabled}
                          onChange={e =>
                            setModelEnabled(activeTab, m.id, e.target.checked)
                          }
                          className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <input
                          type="number"
                          className="w-20 rounded-full border border-zinc-200 bg-white px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-950"
                          value={m.sort_order}
                          onChange={e =>
                            setModelSort(activeTab, m.id, Number(e.target.value) || 0)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
